const SCHEDULE_TYPE_CLOSED = 4;

const MINUTES_PER_DAY = 24 * 60;
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row && row[key] !== undefined) return row[key];
  }
  return undefined;
};

const parseClockMinutes = (value) => {
  if (value == null) return null;
  const match = /(?:T|^|\s)(\d{1,2}):(\d{2})/.exec(String(value));
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h > 24 || m > 59) return null;
  return h * 60 + m;
};

const buildWindow = (openValue, closeValue) => {
  const openMinutes = parseClockMinutes(openValue);
  let closeMinutes = parseClockMinutes(closeValue);
  if (openMinutes == null || closeMinutes == null) return null;
  if (closeMinutes >= MINUTES_PER_DAY - 1) closeMinutes = MINUTES_PER_DAY;
  if (closeMinutes <= openMinutes) closeMinutes += MINUTES_PER_DAY;
  return { openMinutes, closeMinutes };
};

const toDayId = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 7 ? n : null;
};

export const dayIdFromDate = (date) => {
  const d = date.getDay();
  return d === 0 ? 7 : d;
};

export const normalizeOnlineOrderingSchedule = (payload) => {
  const storeTimings = pick(payload, 'storeTimings', 'StoreTimings');
  const storeTypeTimings = pick(payload, 'storeTypeTimings', 'StoreTypeTimings');
  if (!Array.isArray(storeTimings) && !Array.isArray(storeTypeTimings)) return null;

  const location = {};
  (Array.isArray(storeTimings) ? storeTimings : []).forEach((row) => {
    const dayId = toDayId(pick(row, 'dayId', 'DayId'));
    if (!dayId) return;
    const window = buildWindow(pick(row, 'openingTime', 'OpeningTime'), pick(row, 'closingTime', 'ClosingTime'));
    const closed = pick(row, 'closed', 'Closed') === true || !window;
    location[dayId] = { closed, windows: closed ? [] : [window] };
  });

  const services = {};
  (Array.isArray(storeTypeTimings) ? storeTypeTimings : []).forEach((row) => {
    const serviceTypeId = Number(pick(row, 'serviceTypeId', 'ServiceTypeId'));
    const dayId = toDayId(pick(row, 'dayId', 'DayId'));
    if (!Number.isFinite(serviceTypeId) || !dayId) return;
    const windows = [
      buildWindow(pick(row, 'firstOpeningTime', 'FirstOpeningTime'), pick(row, 'firstClosingTime', 'FirstClosingTime')),
      buildWindow(pick(row, 'secondOpeningTime', 'SecondOpeningTime'), pick(row, 'secondClosingTime', 'SecondClosingTime')),
    ].filter(Boolean);
    const closed = Number(pick(row, 'scheduleType', 'ScheduleType')) === SCHEDULE_TYPE_CLOSED || !windows.length;
    if (!services[serviceTypeId]) services[serviceTypeId] = {};
    services[serviceTypeId][dayId] = { closed, windows: closed ? [] : windows };
  });

  return { location, services };
};

const isOpenAt = (entry, minutes) =>
  Boolean(entry) && !entry.closed && entry.windows.some((w) => minutes >= w.openMinutes && minutes < w.closeMinutes);

export const isWithinOnlineOrderingSchedule = (schedule, instant, serviceTypeId) => {
  if (!schedule) return true;
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) return false;
  const dayId = dayIdFromDate(instant);
  const minutes = instant.getHours() * 60 + instant.getMinutes();

  const locationEntry = schedule.location[dayId];
  if (locationEntry && !isOpenAt(locationEntry, minutes)) return false;

  const serviceEntry = schedule.services[Number(serviceTypeId)]?.[dayId];
  if (serviceEntry && !isOpenAt(serviceEntry, minutes)) return false;

  return true;
};

const formatClock = (totalMinutes) => {
  const minutesInDay = totalMinutes % MINUTES_PER_DAY;
  const h24 = Math.floor(minutesInDay / 60);
  const m = minutesInDay % 60;
  const suffix = h24 >= 12 ? 'pm' : 'am';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m ? `${h12}.${String(m).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
};

const labelForEntry = (entry) => {
  if (!entry || entry.closed || !entry.windows.length) return 'Closed';
  return entry.windows.map((w) => `${formatClock(w.openMinutes)} - ${formatClock(w.closeMinutes)}`).join(' & ');
};

export const buildOpeningHoursLines = (schedule, serviceTypeId) => {
  if (!schedule) return [];
  const serviceDays = schedule.services[Number(serviceTypeId)] || {};
  const labels = DAY_NAMES.map((_, index) => {
    const dayId = index + 1;
    const locationEntry = schedule.location[dayId];
    const serviceEntry = serviceDays[dayId];
    if (!locationEntry && !serviceEntry) return null;
    if (locationEntry?.closed) return 'Closed';
    return labelForEntry(serviceEntry || locationEntry);
  });

  const lines = [];
  let start = 0;
  while (start < labels.length) {
    if (labels[start] == null) {
      start += 1;
      continue;
    }
    let end = start;
    while (end + 1 < labels.length && labels[end + 1] === labels[start]) end += 1;
    const dayText = start === end ? DAY_NAMES[start] : `${DAY_NAMES[start]} - ${DAY_NAMES[end]}`;
    lines.push(`${dayText} ${labels[start]}`);
    start = end + 1;
  }
  return lines;
};